import { toLocalDateStr } from "@/utils/date";
import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { CustomFieldsCard } from "@/components/CustomFieldsCard";
import { JobBoard } from "@/components/JobBoard";
import { JobChat } from "@/components/JobChat";
import { ProjectFormsCard } from "@/components/ProjectFormsCard";
import { ProcurementListsCard } from "@/components/procurement/ProcurementListsCard";
import { ProductionSheetsCard } from "@/components/production/ProductionSheetsCard";
import { SendToProductionButton } from "@/components/production/SendToProductionButton";
import { LifecycleStagePill } from "@/components/LifecycleStagePill";
import { ProjectStageCard } from "@/components/ProjectStageCard";
import { SendForBidDialog } from "@/components/bids/SendForBidDialog";
import { SubcontractorQuotesLog } from "@/components/bids/SubcontractorQuotesLog";
import { ProjectBillingPanel } from "@/components/billing/ProjectBillingPanel";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  ArrowLeft,
  Calendar,
  Upload,
  Image,
  FileText,
  File,
  Plus,
  Trash2,
  Users,
  GripVertical,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Download,
  Eye,
  Clock,
  MapPin,
  X,
  Copy,
  ClipboardPaste,
  Send,
  Check,
  Gavel,
  WandSparkles
} from "lucide-react";

// Calendar helpers
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function ProjectDetailPage() {
  const { customerId, projectId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [project, setProject] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [files, setFiles] = useState([]);
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("schedule");
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Modals
  const [showEventModal, setShowEventModal] = useState(false);
  const [showTeamSidebar, setShowTeamSidebar] = useState(false);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  
  // Copy/Paste state
  const [copiedEvent, setCopiedEvent] = useState(null);
  const [draggingEvent, setDraggingEvent] = useState(null);
  
  // Publish state
  const [selectedPublishIds, setSelectedPublishIds] = useState([]);
  const [publishing, setPublishing] = useState(false);
  
  // New event form
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    start_date: "",
    end_date: "",
    start_time: "08:00",
    end_time: "17:00",
    assigned_to: [],
    color: "#6366f1"
  });
  
  // Edit event
  const [editingEvent, setEditingEvent] = useState(null);

  useEffect(() => {
    fetchData();
  }, [projectId, customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [projectRes, customerRes, filesRes, scheduleRes, teamRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/customers/${customerId}`),
        axios.get(`${API}/customers/projects/${projectId}/files`),
        axios.get(`${API}/customers/projects/${projectId}/schedule`),
        axios.get(`${API}/team`)
      ]);
      
      setProject(projectRes.data);
      setCustomer(customerRes.data);
      setFiles(filesRes.data || []);
      setScheduleEvents(scheduleRes.data || []);
      setTeamMembers(teamRes.data || []);
    } catch (error) {
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get calendar days for current month
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    
    // Previous month padding
    const prevMonth = new Date(year, month, 0);
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month padding
    const remainingDays = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return scheduleEvents.filter(event => {
      const startDate = event.start_date?.split("T")[0];
      const endDate = event.end_date?.split("T")[0] || startDate;
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  // Handle date click - open event modal
  const handleDateClick = (date) => {
    const dateStr = toLocalDateStr(date);
    setSelectedDate(date);
    setNewEvent({
      title: "",
      description: "",
      start_date: dateStr,
      end_date: dateStr,
      assigned_to: [],
      color: "#6366f1"
    });
    setEditingEvent(null);
    setShowEventModal(true);
  };

  // Handle event click - edit
  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setEditingEvent(event);
    setNewEvent({
      title: event.title,
      description: event.description || "",
      start_date: event.start_date?.split("T")[0] || "",
      end_date: event.end_date?.split("T")[0] || event.start_date?.split("T")[0] || "",
      assigned_to: event.assigned_to || [],
      color: event.color || "#6366f1"
    });
    setShowEventModal(true);
  };

  // Save event (create or update)
  const handleSaveEvent = async () => {
    if (!newEvent.title.trim()) {
      toast.error("Please enter an event title");
      return;
    }
    
    setSavingEvent(true);
    try {
      if (editingEvent) {
        // Update existing event
        await axios.put(`${API}/customers/projects/${projectId}/schedule/${editingEvent.event_id}`, newEvent);
        toast.success("Event updated!");
      } else {
        // Create new event
        await axios.post(`${API}/customers/projects/${projectId}/schedule`, newEvent);
        toast.success("Event created!");
      }
      
      // Refresh schedule
      const scheduleRes = await axios.get(`${API}/customers/projects/${projectId}/schedule`);
      setScheduleEvents(scheduleRes.data || []);
      setShowEventModal(false);
      setEditingEvent(null);
    } catch (error) {
      toast.error("Failed to save event");
    } finally {
      setSavingEvent(false);
    }
  };

  // Delete event
  const handleDeleteEvent = async () => {
    if (!editingEvent) return;
    
    try {
      await axios.delete(`${API}/customers/projects/${projectId}/schedule/${editingEvent.event_id}`);
      toast.success("Event deleted");
      
      const scheduleRes = await axios.get(`${API}/customers/projects/${projectId}/schedule`);
      setScheduleEvents(scheduleRes.data || []);
      setShowEventModal(false);
      setEditingEvent(null);
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  // Copy event to clipboard
  const handleCopyEvent = (event) => {
    setCopiedEvent({
      title: event.title,
      description: event.description,
      start_time: event.start_time || "08:00",
      end_time: event.end_time || "17:00",
      assigned_to: event.assigned_to || [],
      color: event.color || "#6366f1"
    });
    toast.success("Event copied! Right-click on a date to paste");
  };

  // Paste event to a date
  const handlePasteEvent = async (date) => {
    if (!copiedEvent) {
      toast.error("No event copied. Right-click an event to copy it first.");
      return;
    }
    
    const dateStr = toLocalDateStr(date);
    
    try {
      await axios.post(`${API}/customers/projects/${projectId}/schedule`, {
        ...copiedEvent,
        start_date: dateStr,
        end_date: dateStr
      });
      toast.success("Event pasted!");
      
      const scheduleRes = await axios.get(`${API}/customers/projects/${projectId}/schedule`);
      setScheduleEvents(scheduleRes.data || []);
    } catch (error) {
      toast.error("Failed to paste event");
    }
  };

  // Drag start
  const handleDragStart = (event, e) => {
    e.stopPropagation();
    setDraggingEvent(event);
    e.dataTransfer.setData("eventId", event.event_id);
    e.dataTransfer.effectAllowed = "move";
  };

  // Drop on date (move event)
  const handleDrop = async (date, e) => {
    e.preventDefault();
    const eventId = e.dataTransfer.getData("eventId");
    const userId = e.dataTransfer.getData("userId");
    
    // If dropping a team member, add them to the date
    if (userId && !eventId) {
      handleDateClick(date);
      setNewEvent(prev => ({
        ...prev,
        assigned_to: [...(prev.assigned_to || []), userId]
      }));
      return;
    }
    
    if (!eventId || !draggingEvent) return;
    
    const dateStr = toLocalDateStr(date);
    
    // Check if it's a copy (ctrl/cmd held) or move
    const isCopy = e.ctrlKey || e.metaKey;
    
    try {
      if (isCopy) {
        // Create a copy of the event
        await axios.post(`${API}/customers/projects/${projectId}/schedule`, {
          title: draggingEvent.title,
          description: draggingEvent.description,
          start_date: dateStr,
          end_date: dateStr,
          start_time: draggingEvent.start_time || "08:00",
          end_time: draggingEvent.end_time || "17:00",
          assigned_to: draggingEvent.assigned_to || [],
          color: draggingEvent.color || "#6366f1"
        });
        toast.success("Event copied to new date!");
      } else {
        // Move the event
        await axios.put(`${API}/customers/projects/${projectId}/schedule/${eventId}`, {
          start_date: dateStr,
          end_date: dateStr
        });
        toast.success("Event moved!");
      }
      
      const scheduleRes = await axios.get(`${API}/customers/projects/${projectId}/schedule`);
      setScheduleEvents(scheduleRes.data || []);
    } catch (error) {
      toast.error("Failed to move event");
    } finally {
      setDraggingEvent(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = e.ctrlKey || e.metaKey ? "copy" : "move";
  };

  // Publish selected events to main schedule
  const handlePublishToMainSchedule = async () => {
    if (selectedPublishIds.length === 0) {
      toast.error("Select events to publish");
      return;
    }
    
    setPublishing(true);
    try {
      // Create schedules in the main schedule for each selected event
      const selectedEvents = scheduleEvents.filter(e => selectedPublishIds.includes(e.event_id));
      
      for (const event of selectedEvents) {
        await axios.post(`${API}/schedules`, {
          project_id: projectId,
          title: event.title,
          description: event.description,
          date: event.start_date?.split("T")[0],
          start_time: event.start_time || "08:00",
          end_time: event.end_time || "17:00",
          assigned_users: event.assigned_to || [],
          notes: `Published from project schedule. Event ID: ${event.event_id}`
        });
        
        // Mark the event as published
        await axios.put(`${API}/customers/projects/${projectId}/schedule/${event.event_id}`, {
          is_published: true
        });
      }
      
      toast.success(`${selectedPublishIds.length} event(s) published to main schedule!`);
      setSelectedPublishIds([]);
      
      // Refresh schedule
      const scheduleRes = await axios.get(`${API}/customers/projects/${projectId}/schedule`);
      setScheduleEvents(scheduleRes.data || []);
    } catch (error) {
      toast.error("Failed to publish events");
    } finally {
      setPublishing(false);
    }
  };

  // Toggle event selection for publishing
  const toggleEventSelection = (eventId) => {
    setSelectedPublishIds(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  // Toggle team member assignment
  const toggleTeamMember = (userId) => {
    const current = newEvent.assigned_to || [];
    if (current.includes(userId)) {
      setNewEvent({ ...newEvent, assigned_to: current.filter(id => id !== userId) });
    } else {
      setNewEvent({ ...newEvent, assigned_to: [...current, userId] });
    }
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", file.type.startsWith("image/") ? "photo" : "document");
      
      await axios.post(`${API}/customers/projects/${projectId}/files`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("File uploaded!");
      
      // Refresh files
      const filesRes = await axios.get(`${API}/customers/projects/${projectId}/files`);
      setFiles(filesRes.data || []);
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Delete file
  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/customers/projects/${projectId}/files/${fileId}`);
      toast.success("File deleted");
      setFiles(files.filter(f => f.file_id !== fileId));
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  // Get team member name by ID
  const getTeamMemberName = (userId) => {
    const member = teamMembers.find(m => m.user_id === userId);
    return member ? member.name : "Unknown";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Project not found</p>
        <Button onClick={() => navigate(`/customers/${customerId}`)} className="mt-4">
          Back to Customer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div 
              className="w-4 h-12 rounded-full"
              style={{ backgroundColor: project.color || "#6366f1" }}
            />
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {project.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                <LifecycleStagePill project={project} canEdit={["owner", "project_manager", "office_admin"].includes(user?.role)} onChanged={fetchData} />
                <span>{customer?.first_name} {customer?.last_name}</span>
                {project.address ? (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {project.address}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-600 text-xs" data-testid="address-missing-badge"><MapPin className="w-3 h-3" /> Install address missing</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 xl:w-auto">
          {user?.role === "owner" && <Button onClick={() => navigate(`/customers/${customerId}/projects/${projectId}/design-estimate`)} className="bg-slate-950 text-white hover:bg-slate-800" data-testid="open-ai-design-wizard-button"><WandSparkles className="mr-2 h-4 w-4 text-amber-400" />AI Design & Estimate</Button>}
          {["owner", "project_manager"].includes(user?.role) && <SendToProductionButton projectId={projectId} testId="send-project-to-cnc-button" />}
          {["owner", "project_manager", "office_admin"].includes(user?.role) && <Button onClick={() => setShowBidDialog(true)} data-testid="btn-send-for-bid-trigger"><Gavel className="mr-2 h-4 w-4" />Send for Bid</Button>}
          <Button variant="outline" onClick={() => setShowTeamSidebar(true)} data-testid="open-team-sidebar">
            <Users className="w-4 h-4 mr-2" /> Team
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/photo-gallery/${projectId}`)} data-testid="photo-gallery-btn">
            Photos
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/job-costing/${projectId}`)} data-testid="job-costing-btn">
            Job Costing
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/materials/${projectId}`)} data-testid="materials-btn">
            Materials
          </Button>
        </div>
      </div>

      <ProjectStageCard project={project} onProjectChange={setProject} />

      <ProjectBillingPanel projectId={projectId} userRole={user?.role} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="schedule" className="gap-2">
            <Calendar className="w-4 h-4" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="files" className="gap-2">
            <FileText className="w-4 h-4" />
            Files ({files.length})
          </TabsTrigger>
        </TabsList>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="mt-4 space-y-4">
          {/* Unpublished Events Banner */}
          {scheduleEvents.filter(e => !e.is_published).length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-600" />
                    <span className="text-amber-800 font-medium">
                      {scheduleEvents.filter(e => !e.is_published).length} unpublished event(s)
                    </span>
                    <span className="text-amber-600 text-sm">
                      - Select and publish to add to main schedule
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        const unpublished = scheduleEvents.filter(e => !e.is_published).map(e => e.event_id);
                        setSelectedPublishIds(
                          selectedPublishIds.length === unpublished.length ? [] : unpublished
                        );
                      }}
                    >
                      {selectedPublishIds.length === scheduleEvents.filter(e => !e.is_published).length 
                        ? 'Deselect All' 
                        : 'Select All'}
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handlePublishToMainSchedule}
                      disabled={selectedPublishIds.length === 0 || publishing}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {publishing ? "Publishing..." : `Publish (${selectedPublishIds.length})`}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Copy/Paste Hint */}
          {copiedEvent && (
            <div className="flex items-center gap-2 text-sm text-indigo-600 bg-indigo-50 px-3 py-2 rounded-lg">
              <ClipboardPaste className="w-4 h-4" />
              <span>Event copied: &quot;{copiedEvent.title}&quot; - Right-click on a date to paste</span>
              <Button variant="ghost" size="sm" onClick={() => setCopiedEvent(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                  <Button variant="outline" size="icon" onClick={goToPreviousMonth} data-testid="project-calendar-previous-month-btn">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <h2 className="min-w-[140px] text-center text-base font-semibold sm:min-w-[180px] sm:text-lg" data-testid="project-calendar-current-month">
                    {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <Button variant="outline" size="icon" onClick={goToNextMonth} data-testid="project-calendar-next-month-btn">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={goToToday} data-testid="project-calendar-today-btn">
                    Today
                  </Button>
                </div>
                <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
                  <span className="hidden text-xs text-slate-500 lg:inline" data-testid="project-calendar-drag-hint">
                    Tip: Drag events to move • Ctrl+Drag to copy • Right-click for more options
                  </span>
                  <Button onClick={() => handleDateClick(new Date())} data-testid="add-event-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Event
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Calendar Grid */}
              <div className="border rounded-lg overflow-hidden">
                {/* Days header */}
                <div className="grid grid-cols-7 bg-slate-50 border-b">
                  {DAYS.map(day => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-slate-600">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar days */}
                <div className="grid grid-cols-7">
                  {getCalendarDays().map((day, index) => {
                    const events = getEventsForDate(day.date);
                    const today = isToday(day.date);
                    
                    return (
                      <ContextMenu key={index}>
                        <ContextMenuTrigger>
                          <div
                            className={`min-h-[100px] p-1 border-b border-r cursor-pointer hover:bg-slate-50 transition-colors ${
                              !day.isCurrentMonth ? "bg-slate-50/50" : ""
                            } ${draggingEvent ? "hover:bg-indigo-50 hover:border-indigo-300" : ""}`}
                            onClick={() => handleDateClick(day.date)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(day.date, e)}
                          >
                            <div className={`text-sm p-1 ${
                              today 
                                ? "bg-indigo-600 text-white rounded-full w-7 h-7 flex items-center justify-center" 
                                : day.isCurrentMonth ? "text-slate-700" : "text-slate-400"
                            }`}>
                              {day.date.getDate()}
                            </div>
                            
                            {/* Events */}
                            <div className="space-y-1 mt-1">
                              {events.slice(0, 3).map(event => (
                                <ContextMenu key={event.event_id}>
                                  <ContextMenuTrigger>
                                    <div
                                      className={`text-xs p-1 rounded truncate cursor-grab hover:opacity-80 flex items-center gap-1 ${
                                        event.is_published ? "ring-1 ring-green-500" : ""
                                      }`}
                                      style={{ backgroundColor: event.color || "#6366f1", color: "white" }}
                                      onClick={(e) => handleEventClick(event, e)}
                                      draggable
                                      onDragStart={(e) => handleDragStart(event, e)}
                                      title={`${event.title}${event.is_published ? " (Published)" : ""}\nDrag to move • Ctrl+Drag to copy`}
                                    >
                                      {!event.is_published && (
                                        <input
                                          type="checkbox"
                                          checked={selectedPublishIds.includes(event.event_id)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleEventSelection(event.event_id);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-3 h-3 rounded"
                                        />
                                      )}
                                      {event.is_published && <Check className="w-3 h-3" />}
                                      <span className="truncate">{event.title}</span>
                                    </div>
                                  </ContextMenuTrigger>
                                  <ContextMenuContent>
                                    <ContextMenuItem onClick={() => handleEventClick(event, { stopPropagation: () => {} })}>
                                      <Eye className="w-4 h-4 mr-2" />
                                      View/Edit
                                    </ContextMenuItem>
                                    <ContextMenuItem onClick={() => handleCopyEvent(event)}>
                                      <Copy className="w-4 h-4 mr-2" />
                                      Copy Event
                                    </ContextMenuItem>
                                    {!event.is_published && (
                                      <ContextMenuItem onClick={() => {
                                        setSelectedPublishIds([event.event_id]);
                                        handlePublishToMainSchedule();
                                      }}>
                                        <Send className="w-4 h-4 mr-2" />
                                        Publish to Main Schedule
                                      </ContextMenuItem>
                                    )}
                                    <ContextMenuItem 
                                      className="text-red-600"
                                      onClick={async () => {
                                        if (window.confirm("Delete this event?")) {
                                          await axios.delete(`${API}/customers/projects/${projectId}/schedule/${event.event_id}`);
                                          toast.success("Event deleted");
                                          const scheduleRes = await axios.get(`${API}/customers/projects/${projectId}/schedule`);
                                          setScheduleEvents(scheduleRes.data || []);
                                        }
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </ContextMenuItem>
                                  </ContextMenuContent>
                                </ContextMenu>
                              ))}
                              {events.length > 3 && (
                                <div className="text-xs text-slate-500 pl-1">
                                  +{events.length - 3} more
                                </div>
                              )}
                            </div>
                          </div>
                        </ContextMenuTrigger>
                        <ContextMenuContent>
                          <ContextMenuItem onClick={() => handleDateClick(day.date)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Event
                          </ContextMenuItem>
                          {copiedEvent && (
                            <ContextMenuItem onClick={() => handlePasteEvent(day.date)}>
                              <ClipboardPaste className="w-4 h-4 mr-2" />
                              Paste Event
                            </ContextMenuItem>
                          )}
                        </ContextMenuContent>
                      </ContextMenu>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files" className="mt-4">
          {/* Custom Fields */}
          <CustomFieldsCard entityType="project" entityId={projectId} />

          {/* Job Board */}
          <JobBoard projectId={projectId} />

          <SubcontractorQuotesLog projectId={projectId} />

          {/* Attached Forms */}
          <ProjectFormsCard projectId={projectId} customerId={customerId} />

          {/* Material Procurement */}
          <ProcurementListsCard projectId={projectId} customerId={customerId} />

          {/* Production Sheets (cut lists) */}
          <ProductionSheetsCard projectId={projectId} />

          {/* Job Chat */}
          <Card>
            <JobChat projectId={projectId} />
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Project Files</CardTitle>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-file-btn"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {uploading ? "Uploading..." : "Upload File"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {files.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {files.map(file => (
                    <div
                      key={file.file_id}
                      className="border rounded-lg p-3 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded flex items-center justify-center ${
                          (() => { if (file.is_image) return "bg-blue-100"; if (file.is_pdf) return "bg-red-100"; return "bg-slate-100"; })()
                        }`}>
                          {file.is_image ? (
                            <Image className="w-5 h-5 text-blue-600" />
                          ) : file.is_pdf ? (
                            <FileText className="w-5 h-5 text-red-600" />
                          ) : (
                            <File className="w-5 h-5 text-slate-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate" title={file.filename}>
                            {file.filename}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatFileSize(file.size)} • {file.uploaded_by_name}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => handleDeleteFile(file.file_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No files yet</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Upload photos, drawings, documents, and more
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? "Edit Event" : "New Event"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Event Title *</Label>
              <Input
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                placeholder="e.g., Cabinet Installation"
                className="mt-1"
                data-testid="event-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Event details..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newEvent.start_date}
                  onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={newEvent.end_date}
                  onChange={(e) => setNewEvent({ ...newEvent, end_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={newEvent.start_time}
                  onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={newEvent.end_time}
                  onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Event Color</Label>
              <div className="flex gap-2 mt-2">
                {["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"].map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      newEvent.color === color ? "border-slate-900 scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewEvent({ ...newEvent, color })}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Assign Team Members</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {teamMembers.map(member => (
                  <label
                    key={member.user_id}
                    className={`flex items-center gap-3 p-2 rounded border cursor-pointer transition-colors ${
                      newEvent.assigned_to?.includes(member.user_id)
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <Checkbox
                      checked={newEvent.assigned_to?.includes(member.user_id)}
                      onCheckedChange={() => toggleTeamMember(member.user_id)}
                    />
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                      {member.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{member.name}</p>
                      <p className="text-xs text-slate-500 capitalize">{member.role?.replace("_", " ")}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            {editingEvent && (
              <Button variant="destructive" onClick={handleDeleteEvent}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEventModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEvent} disabled={savingEvent || !newEvent.title.trim()}>
                {savingEvent ? "Saving..." : editingEvent ? "Update" : "Create"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Sidebar */}
      <Sheet open={showTeamSidebar} onOpenChange={setShowTeamSidebar}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Members
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-3">
            <p className="text-sm text-slate-500">
              Drag team members onto calendar dates to assign them to events
            </p>
            {teamMembers.map(member => (
              <div
                key={member.user_id}
                className="flex items-center gap-3 p-3 border rounded-lg cursor-grab hover:shadow-sm transition-shadow"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("userId", member.user_id);
                  e.dataTransfer.setData("userName", member.name);
                }}
              >
                <GripVertical className="w-4 h-4 text-slate-400" />
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-medium">
                  {member.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">{member.name}</p>
                  <p className="text-sm text-slate-500 capitalize">{member.role?.replace("_", " ")}</p>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
      <SendForBidDialog open={showBidDialog} onOpenChange={setShowBidDialog} projectId={projectId} leadId={customerId} projectName={project.name} scopeSummary={project.description || ""} />
    </div>
  );
}
